// How an ember roster card leaves the strip when its member dies. PIXI-free (like
// roster_damage.ts and roster_speech_lane.ts beside it), so the timings and the shard motion can be
// unit-tested without a renderer; ember_roster.ts owns the objects and draws what this decides.
//
// WHY A DEATH GETS A SECOND AND A HALF. Until now a member who died simply stopped being in the
// roster broadcast, and their card vanished between two ticks -- the strip just had one card fewer,
// with nothing to say that somebody had been lost. The camp is small enough that losing a defender
// matters, so the card is given an exit:
//
//   - the colour drains out of it until it is completely grey (the member is gone),
//   - then the card itself breaks apart and the pieces fall away,
//   - and only once the last piece has gone does the strip close the gap.
//
// The card holds its COLUMN for the whole of it. Letting the survivors slide over immediately would
// put a live card on top of the one that is breaking up, and would also mean the strip shrank before
// the player had seen which card it was.

export const GREY_MS = 500
export const SHATTER_MS = 1000
export const DEATH_MS = GREY_MS + SHATTER_MS

// Shard size in card-local pixels. A 70x96 card breaks into a 5x7 grid of chunks this big -- big
// enough to still read as pieces OF THE CARD (a face, a corner of the frame) rather than as noise,
// and few enough that a wipe costing several cards at once is still a few hundred sprites.
export const FRAGMENT_PX = 14

// Shard motion, in card-local pixels per second (the widget's own scale applies on top).
export const GRAVITY = 900
// Sideways burst away from the card's centre, and the kick that throws the pieces up first, so the
// card bursts rather than just sagging.
export const BURST_X = 70
export const KICK_Y = 150
export const SPIN_DEG = 420
// The crack spreads from the middle outward: an edge piece waits this long behind a centre one.
export const STAGGER_MS = 90
// Share of a shard's flight it stays fully opaque for before fading out.
export const FADE_FROM = 0.35

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v)

// How far the colour has drained, 0..1. Eased so it starts quickly (something clearly happened) and
// arrives at completely grey rather than creeping there.
export const greyAt = (elapsedMs: number): number => {
    const p = clamp01(elapsedMs / GREY_MS)
    return 1 - (1 - p) * (1 - p)
}

export const isShattering = (elapsedMs: number): boolean => elapsedMs >= GREY_MS
export const isFinished = (elapsedMs: number): boolean => elapsedMs >= DEATH_MS

export interface FragmentSeed {
    // Grid position and the size of this piece (edge pieces are clipped short).
    col: number
    row: number
    x: number
    y: number
    w: number
    h: number
    // Initial velocity (px/s), spin (deg/s) and how long this piece waits before it lets go.
    vx: number
    vy: number
    spin: number
    delayMs: number
}

export interface FragmentMotion {
    dx: number
    dy: number
    angle: number
    alpha: number
}

/**
 * How big to ask for a texture that a card of `size` is going to be photographed into, so that the
 * photograph is never SMALLER than the card.
 *
 * A pixi render texture keeps its size in whole device pixels (`size * resolution`) and reports it
 * back divided by that resolution. So whenever `size * resolution` has to round DOWN, the texture
 * measures a fraction less than what was asked for -- 70 in, 69.84 out at the 1.26 this renderer
 * runs at, because its resolution carries the letterbox scale. The shards tile the card EXACTLY (the
 * right-hand column ends at 70), so that fraction is the difference between a frame that fits its
 * base texture and one pixi refuses to construct -- and it is constructed from the render loop,
 * where a throw stops the ticker and freezes the client for good.
 *
 * Rounding up leaves at most a device pixel of transparent padding on the right and bottom edges,
 * sampled only by the outermost shards of a card that is already flying apart.
 */
export const captureExtent = (size: number, resolution: number): number => {
    const scale = resolution > 0 ? resolution : 1
    return Math.ceil(size * scale) / scale
}

// Slice a card into shards and give each one its flight. `rand` is injectable so a test can pin the
// randomness; it must return 0..1.
export const seedFragments = (
    width: number,
    height: number,
    rand: () => number = Math.random,
    size: number = FRAGMENT_PX,
): FragmentSeed[] => {
    const cols = Math.max(1, Math.ceil(width / size))
    const rows = Math.max(1, Math.ceil(height / size))
    const seeds: FragmentSeed[] = []
    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            const x = col * size
            const y = row * size
            // Where this piece sits relative to the card's centre, -1..1 on each axis. It decides
            // which way the piece is thrown, so the card comes apart from the middle.
            const nx = cols > 1 ? (col - (cols - 1) / 2) / ((cols - 1) / 2) : 0
            const ny = rows > 1 ? (row - (rows - 1) / 2) / ((rows - 1) / 2) : 0
            const spread = Math.min(1, Math.sqrt(nx * nx + ny * ny))
            seeds.push({
                col,
                row,
                x,
                y,
                // Clipped at the right/bottom edge so the shards tile the card exactly.
                w: Math.min(size, width - x),
                h: Math.min(size, height - y),
                vx: nx * BURST_X * (0.6 + rand() * 0.8),
                // Up first (negative y), hardest at the top of the card, then gravity takes over.
                vy: -KICK_Y * (0.45 + rand() * 0.7) + ny * 30,
                spin: (rand() - 0.5) * 2 * SPIN_DEG,
                delayMs: spread * STAGGER_MS,
            })
        }
    }
    return seeds
}

// Where a shard is, and how visible, `shatterMs` into the shatter (i.e. measured from GREY_MS, not
// from the start of the death). Null before it has let go, so the caller can leave it hidden.
export const fragmentAt = (shatterMs: number, seed: FragmentSeed): FragmentMotion | null => {
    if (shatterMs < seed.delayMs) {
        return null
    }
    const flightMs = SHATTER_MS - seed.delayMs
    const p = clamp01((shatterMs - seed.delayMs) / flightMs)
    const t = (shatterMs - seed.delayMs) / 1000
    return {
        dx: seed.vx * t,
        dy: seed.vy * t + 0.5 * GRAVITY * t * t,
        angle: seed.spin * t,
        alpha: p < FADE_FROM ? 1 : 1 - (p - FADE_FROM) / (1 - FADE_FROM),
    }
}
